import { useState, ReactNode, useEffect, useRef, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContextDefinition';
import { usePostHogIdentification } from '../hooks/usePostHogIdentification';
import { Analytics } from '../lib/analytics';
import { apiClient } from '../services/api-client';

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useUser();
  const navigate = useNavigate();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'sign-in' | 'sign-up'>('sign-in');
  const [isPremium, setIsPremium] = useState(false);
  const [isPremiumLoaded, setIsPremiumLoaded] = useState(false);
  const [authRedirectUrl, setAuthRedirectUrl] = useState<string | undefined>();
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(null);
  const wasSignedInRef = useRef(false);
  const wasModalOpenRef = useRef(false);
  const modalModeRef = useRef<'sign-in' | 'sign-up'>('sign-in');
  const hasCheckedInviteCodeRef = useRef(false);
  
  // Monitor auth state for PostHog user identification
  usePostHogIdentification();
  
  // Track sign-up success when user signs in after having the modal open in sign-up mode
  useEffect(() => {
    // Update refs to track state changes
    if (isAuthModalOpen !== wasModalOpenRef.current || authModalMode !== modalModeRef.current) {
      wasModalOpenRef.current = isAuthModalOpen;
      modalModeRef.current = authModalMode;
    }
    
    // Check if user just signed in (transition from not signed in to signed in)
    if (isLoaded && isSignedIn && !wasSignedInRef.current) {
      // If the modal was open in sign-up mode when they signed in, track sign-up success
      if (wasModalOpenRef.current && modalModeRef.current === 'sign-up') {
        Analytics.trackSignUpSuccess({
          source: 'modal',
          method: 'email' // Default to email, as we can't determine the actual method from Clerk
        });
      }
    }
    
    // Update the ref to track the current sign-in state
    if (isLoaded) {
      wasSignedInRef.current = isSignedIn;
    }
  }, [isSignedIn, isLoaded, isAuthModalOpen, authModalMode]);

  // Fetch isPremium from backend auth profile (IS-INV-034: backend is sole authority)
  useEffect(() => {
    let active = true;
    if (!isLoaded) return () => { active = false; };
    if (!isSignedIn) {
      setIsPremium(false);
      setIsPremiumLoaded(false);
      return () => { active = false; };
    }
    apiClient.get<{ success: boolean; isPremium: boolean }>('/api/v1/auth/profile')
      .then(data => { if (active) { setIsPremium(data.isPremium ?? false); setIsPremiumLoaded(true); } })
      .catch(() => { if (active) { setIsPremium(false); setIsPremiumLoaded(true); } });
    return () => { active = false; };
  }, [isLoaded, isSignedIn]);

  // Check for pending invite code after authentication
  useEffect(() => {
    if (isLoaded && isSignedIn && !hasCheckedInviteCodeRef.current) {
      hasCheckedInviteCodeRef.current = true;

      const pendingInviteCode = localStorage.getItem('pendingInviteCode');
      const isNewRegistration = localStorage.getItem('isNewRegistration');

      if (pendingInviteCode) {
        // Clear the code from localStorage
        localStorage.removeItem('pendingInviteCode');

        // Store registration status for welcome message
        if (isNewRegistration === 'true') {
          localStorage.setItem('showWelcomeMessage', 'true');
          localStorage.removeItem('isNewRegistration');
        }

        // Redirect to the invite handler
        navigate(`/invite/${pendingInviteCode}`);
      }
    }
  }, [isLoaded, isSignedIn, navigate]);
  
  // Reset the invite check ref when user signs out
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      hasCheckedInviteCodeRef.current = false;
    }
  }, [isLoaded, isSignedIn]);
  
  const openAuthModal = useCallback((mode: 'sign-in' | 'sign-up', redirectUrl?: string) => {
    setAuthModalMode(mode);
    setAuthRedirectUrl(redirectUrl);
    setIsAuthModalOpen(true);
  }, []);
  
  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
    setPendingCallback(null);
  };
  
  const openUserProfile = () => {
    setIsUserProfileOpen(true);
  };
  
  const closeUserProfile = () => {
    setIsUserProfileOpen(false);
  };
  
  const requireAuth = (callback: () => void, redirectUrl?: string) => {
    if (!isLoaded) {
      // Wait for auth to load
      return;
    }
    
    if (isSignedIn) {
      // User is authenticated, execute callback immediately
      callback();
    } else {
      // Store callback and open auth modal
      setPendingCallback(() => callback);
      openAuthModal('sign-in', redirectUrl);
    }
  };
  
  // Execute pending callback when user signs in
  if (isSignedIn && pendingCallback) {
    const callback = pendingCallback;
    setPendingCallback(null);
    closeAuthModal();
    // Execute callback after modal closes
    setTimeout(() => callback(), 100);
  }
  
  return (
    <AuthContext.Provider
      value={{
        isAuthModalOpen,
        authModalMode,
        authRedirectUrl,
        isUserProfileOpen,
        isPremium,
        isPremiumLoaded,
        openAuthModal,
        closeAuthModal,
        openUserProfile,
        closeUserProfile,
        requireAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

